import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './CropFrame.module.css';

/**
 * TV studio safe-area guide (Rijnmond request, 2026-08-29).
 *
 * Their studio walls run four PORTRAIT screens side by side; each screen
 * gets one vertical photo at 1080x1920 = 9:16. A 2:3 portrait crop is
 * WIDER than 9:16, so the display system trims ~15% of the width
 * (~7.8% per side), and screen bezels plus decor slats eat further into
 * the edges. This overlay replaces their proposed Photoshop template:
 * the dashed band is what the screen shows, the inner rectangle is where
 * important details are safe from bezels/decor.
 *
 * SCREEN_ASPECT is the per-screen ratio (w/h). DECOR_MARGIN is the
 * fraction of the screen band inset on every side for bezels + decor —
 * a placeholder until Rijnmond supplies real decor measurements.
 * Guides are advisory ("honor if possible"), never a crop constraint.
 */
const TV_SCREEN_ASPECT = 9 / 16;
const TV_DECOR_MARGIN = 0.05;
// Show the toggle only where the guide is meaningful: portrait crops
// wider than the screen (2:3, 3:4 — anything between 9:16 and 1:1).
const TV_GUIDE_KEY = 'matrix-tv-guide';

function TvSafeOverlay({ frameW, frameH }) {
  const bandW = frameH * TV_SCREEN_ASPECT;
  const sideW = Math.max(0, (frameW - bandW) / 2);
  const insetX = sideW + bandW * TV_DECOR_MARGIN;
  const insetY = frameH * TV_DECOR_MARGIN;
  return (
    <div className={styles.tvOverlay}>
      {/* Width the screen cannot show */}
      <div className={clsx(styles.tvOffscreen)} style={{ left: 0, width: `${sideW}px` }} />
      <div className={clsx(styles.tvOffscreen)} style={{ right: 0, width: `${sideW}px` }} />
      {/* The screen itself */}
      <div
        className={styles.tvScreenBand}
        style={{ left: `${sideW}px`, right: `${sideW}px` }}
      >
        <span className={styles.tvLabel}>9:16 screen</span>
      </div>
      {/* Bezel + decor safe area */}
      <div
        className={styles.tvSafeRect}
        style={{
          left: `${insetX}px`,
          right: `${insetX}px`,
          top: `${insetY}px`,
          bottom: `${insetY}px`,
        }}
      >
        <span className={clsx(styles.tvLabel, styles.tvLabelSafe)}>safe</span>
      </div>
    </div>
  );
}

/**
 * 6x6 grid with rule of thirds emphasized.
 * Reused from the original CropOverlay component.
 */
function CropGrid() {
  return (
    <div className={styles.grid}>
      {/* Horizontal lines */}
      <div className={clsx(styles.line, styles.h)} style={{ top: '16.666%' }} />
      <div className={clsx(styles.line, styles.h, styles.thirds)} style={{ top: '33.333%' }} />
      <div className={clsx(styles.line, styles.h, styles.center)} style={{ top: '50%' }} />
      <div className={clsx(styles.line, styles.h, styles.thirds)} style={{ top: '66.666%' }} />
      <div className={clsx(styles.line, styles.h)} style={{ top: '83.333%' }} />

      {/* Vertical lines */}
      <div className={clsx(styles.line, styles.v)} style={{ left: '16.666%' }} />
      <div className={clsx(styles.line, styles.v, styles.thirds)} style={{ left: '33.333%' }} />
      <div className={clsx(styles.line, styles.v, styles.center)} style={{ left: '50%' }} />
      <div className={clsx(styles.line, styles.v, styles.thirds)} style={{ left: '66.666%' }} />
      <div className={clsx(styles.line, styles.v)} style={{ left: '83.333%' }} />
    </div>
  );
}

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const INNER_ROT_CORNERS = ['nw', 'ne', 'se', 'sw'];

/**
 * Fixed-frame crop editor. The frame stays at the target aspect ratio
 * and the user zooms/pans the image underneath. What's visible in the
 * frame IS the final crop.
 *
 * Resize handles map drag distance to zoom level.
 * Rotation zones outside corners/edges control crop tilt.
 * Inner rotation zones inside corners also trigger tilt drag.
 */
export default function CropFrame({
  vizUrl,
  frameW,
  frameH,
  baseScale,
  zoom,
  panX,
  panY,
  cropRotation,
  imgNaturalW,
  imgNaturalH,
  sourceW,
  sourceH,
  onMouseDown,
  onWheel,
  onZoomIn,
  onZoomOut,
  onHandleDrag,
  onRotationDrag,
}) {
  const frameRef = useRef(null);
  const [tvGuide, setTvGuide] = useState(() => {
    try {
      return localStorage.getItem(TV_GUIDE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Attach wheel listener with { passive: false } to ensure preventDefault works
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !onWheel) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  if (!vizUrl || !frameW || !frameH) return null;

  // Portrait crops wider than a 9:16 screen (2:3, 3:4) can lose their
  // edges on the studio walls — only there does the TV guide mean anything.
  const frameAspect = frameW / frameH;
  const tvGuideAvailable = frameAspect > TV_SCREEN_ASPECT + 0.01 && frameAspect < 0.99;
  const toggleTvGuide = () => {
    setTvGuide((v) => {
      const next = !v;
      try {
        localStorage.setItem(TV_GUIDE_KEY, next ? '1' : '0');
      } catch {
        /* preference only */
      }
      return next;
    });
  };

  const s = baseScale * zoom;
  const imgDisplayW = imgNaturalW * s;
  const imgDisplayH = imgNaturalH * s;
  // Crop dimensions reported in SOURCE pixels (what /recrop will produce).
  // If source dims are unknown, we suppress the badge entirely — preview
  // pixels would mislead because they aren't what gets exported.
  const haveSourceDims = sourceW && sourceH && imgNaturalW && imgNaturalH;
  const previewCropW = s > 0 ? frameW / s : 0;
  const previewCropH = s > 0 ? frameH / s : 0;
  const cropW = haveSourceDims ? Math.round(previewCropW * (sourceW / imgNaturalW)) : 0;
  const cropH = haveSourceDims ? Math.round(previewCropH * (sourceH / imgNaturalH)) : 0;

  // Image position: centered in frame, offset by pan
  const imgStyle = {
    width: `${imgDisplayW}px`,
    height: `${imgDisplayH}px`,
    transform: `translate(-50%, -50%) translate(${panX}px, ${panY}px) rotate(${cropRotation}deg)`,
  };

  return (
    <div className={styles.cropContainer}>
      <div className={styles.darkSurround} />
      {/* Wrapper allows handles/rotation zones to overflow outside the frame */}
      <div
        className={styles.frameWrapper}
        style={{ width: `${frameW}px`, height: `${frameH}px` }}
      >
        <div
          ref={frameRef}
          className={styles.frame}
          onMouseDown={(e) => {
            e.preventDefault();
            onMouseDown?.(e);
          }}
        >
          <img
            className={styles.frameImage}
            src={vizUrl}
            alt="crop preview"
            style={imgStyle}
            draggable={false}
          />
          {cropW > 0 && cropH > 0 && (() => {
            // Format the dimensions badge per design: phosphor pill,
            // mono caps, "{w} × {h} px · {ratio} · {MP} MP". Ratio
            // collapses to nearest common ratio when within tolerance,
            // otherwise omitted.
            const mp = (cropW * cropH) / 1_000_000;
            const mpLabel = mp >= 10 ? `${mp.toFixed(1)} MP` : `${mp.toFixed(2)} MP`;
            const aspect = cropW / cropH;
            const COMMON = [
              [3, 2], [2, 3], [4, 3], [3, 4], [16, 9], [9, 16],
              [5, 4], [4, 5], [1, 1],
            ];
            const tol = 0.02;
            const ratioMatch = COMMON.find(([a, b]) =>
              Math.abs(aspect - a / b) / (a / b) < tol,
            );
            const ratioLabel = ratioMatch ? `${ratioMatch[0]}:${ratioMatch[1]}` : null;
            return (
              <div className={styles.dimsBadge}>
                {cropW} × {cropH} px
                {ratioLabel && <span className={styles.dimsDot}>·</span>}
                {ratioLabel && <span>{ratioLabel}</span>}
                <span className={styles.dimsDot}>·</span>
                <span>{mpLabel}</span>
              </div>
            );
          })()}
          <CropGrid />
          {tvGuideAvailable && tvGuide && (
            <TvSafeOverlay frameW={frameW} frameH={frameH} />
          )}
          {tvGuideAvailable && (
            <button
              className={clsx(styles.tvBtn, tvGuide && styles.tvBtnActive)}
              onClick={(e) => { e.stopPropagation(); toggleTvGuide(); }}
              onMouseDown={(e) => e.stopPropagation()}
              title="TV studio guide: dashed band = 9:16 screen, inner rectangle = safe from bezels and decor"
            >
              TV
            </button>
          )}
          {(onZoomIn || onZoomOut) && (
            <div className={styles.zoomControls}>
              <button
                className={styles.zoomBtn}
                onClick={(e) => { e.stopPropagation(); onZoomIn?.(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Zoom in (tighter crop)"
              >
                +
              </button>
              <button
                className={styles.zoomBtn}
                onClick={(e) => { e.stopPropagation(); onZoomOut?.(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Zoom out (wider crop)"
              >
                -
              </button>
            </div>
          )}
          {/* Inner rotation zones - inside the frame corners for tilt drag */}
          {INNER_ROT_CORNERS.map((corner) => (
            <span
              key={`inner-rot-${corner}`}
              className={clsx(styles.innerRotZone, styles[`innerRot_${corner}`])}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRotationDrag?.(corner, e);
              }}
            />
          ))}
        </div>
        {/* Resize handles - positioned relative to frameWrapper (overflow visible) */}
        {HANDLES.map((h) => (
          <span
            key={h}
            className={clsx(styles.handle, styles[`handle_${h}`])}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onHandleDrag?.(h, e);
            }}
          />
        ))}
        {/* Rotation zones - invisible areas outside the frame for rotation drag */}
        {HANDLES.map((zone) => (
          <span
            key={`rot-${zone}`}
            className={clsx(styles.rotationZone, styles[`rot_${zone}`])}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRotationDrag?.(zone, e);
            }}
          />
        ))}
      </div>
    </div>
  );
}
